// Scripture reference parsing and formatting. One implementation for the URL
// router, the search box, [[Neh 1:5]] links in commentary, and the ESV client.
//
// Canonical forms:
//   OSIS      Neh.1.3       Neh.1.3-Neh.1.5     Neh.1  (whole chapter)
//   display   Nehemiah 1:3  Nehemiah 1:3–5      Nehemiah 1
//   slug      nehemiah/1    (chapter pages only; verses ride as #v3 anchors)
import { BOOK_LOOKUP, bookById } from "./books.js";
export class RefError extends Error {
    input;
    constructor(message, input) {
        super(message);
        this.input = input;
        this.name = "RefError";
    }
}
const normBook = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
/**
 * Parse a human or machine reference. Accepts:
 *   "Nehemiah 1", "Neh 1:3", "neh 1.3", "Neh 1:3-5", "Neh 1:3–2:4", "1 John 2:3",
 *   "Neh.1.3", "Neh.1.3-Neh.1.5" (OSIS), "nehemiah-1" and "nehemiah/1" (URLs),
 *   "Jude 5" (single-chapter books take the number as a verse).
 */
export function parseRef(input) {
    const raw = input.trim();
    if (!raw)
        throw new RefError("Empty reference", input);
    // OSIS: Book.C[.V][-Book.C[.V]]
    const osis = /^([1-3]?[A-Za-z]+)\.(\d+)(?:\.(\d+))?(?:-(?:([1-3]?[A-Za-z]+)\.)?(\d+)(?:\.(\d+))?)?$/.exec(raw);
    if (osis) {
        const book = lookupBook(osis[1], input);
        const chapter = num(osis[2]);
        const verse = osis[3] ? num(osis[3]) : undefined;
        let endChapter;
        let endVerse;
        if (osis[5] !== undefined) {
            if (osis[4] && lookupBook(osis[4], input).id !== book.id)
                throw new RefError("Ranges must stay within one book", input);
            if (osis[6] !== undefined) {
                endChapter = num(osis[5]);
                endVerse = num(osis[6]);
            }
            else if (verse !== undefined) {
                endChapter = chapter;
                endVerse = num(osis[5]);
            }
            else {
                endChapter = num(osis[5]);
            }
        }
        return validate({ book: book.id, chapter, verse, endChapter, endVerse }, input);
    }
    // Human / URL: split the book name from the numeric tail.
    const m = /^((?:[1-3]|i{1,3}|first|second|third)?[\s-]*[A-Za-z]+(?:[\s-]+(?:of[\s-]+)?[A-Za-z]+)*)\s*[-/ .:]?\s*(\d[\d\s:.,–—-]*)?$/i.exec(raw);
    if (!m)
        throw new RefError(`Cannot read "${raw}" as a reference`, input);
    const book = lookupBook(m[1], input);
    const tail = (m[2] ?? "").replace(/[–—]/g, "-").replace(/\s+/g, "");
    if (!tail)
        return validate({ book: book.id, chapter: 1 }, input);
    const [startPart, endPart] = tail.split("-", 2);
    const start = startPart.split(/[:.]/).map(num);
    let chapter;
    let verse;
    if (start.length === 1) {
        if (book.chapters === 1) {
            chapter = 1;
            verse = start[0];
        }
        else {
            chapter = start[0];
        }
    }
    else if (start.length === 2) {
        chapter = start[0];
        verse = start[1];
    }
    else {
        throw new RefError(`Cannot read "${raw}" as a reference`, input);
    }
    let endChapter;
    let endVerse;
    if (endPart !== undefined && endPart !== "") {
        const end = endPart.split(/[:.]/).map(num);
        if (end.length === 2) {
            endChapter = end[0];
            endVerse = end[1];
        }
        else if (end.length === 1) {
            if (verse !== undefined) {
                endChapter = chapter;
                endVerse = end[0];
            }
            else {
                endChapter = end[0];
            }
        }
        else {
            throw new RefError(`Cannot read "${raw}" as a reference`, input);
        }
    }
    return validate({ book: book.id, chapter, verse, endChapter, endVerse }, input);
}
function lookupBook(name, input) {
    const key = normBook(name)
        .replace(/^first/, "1")
        .replace(/^second/, "2")
        .replace(/^third/, "3")
        .replace(/^iii(?=[a-z])/, "3")
        .replace(/^ii(?=[a-z])/, "2")
        .replace(/^i(?=[a-z]{2,})(?!s)/, "1"); // "ijohn" → "1john" but leave "isa", "is" alone
    const book = BOOK_LOOKUP.get(key);
    if (!book)
        throw new RefError(`Unknown book "${name.trim()}"`, input);
    return book;
}
function num(s) {
    const n = Number.parseInt(s, 10);
    if (!Number.isFinite(n) || n <= 0)
        throw new RefError(`Bad number "${s}"`, s);
    return n;
}
function validate(ref, input) {
    const book = bookById(ref.book);
    if (!book)
        throw new RefError(`Unknown book "${ref.book}"`, input);
    if (ref.chapter > book.chapters)
        throw new RefError(`${book.name} has ${book.chapters} chapter${book.chapters === 1 ? "" : "s"}`, input);
    if (ref.endChapter !== undefined) {
        if (ref.endChapter > book.chapters)
            throw new RefError(`${book.name} has ${book.chapters} chapters`, input);
        if (ref.endChapter < ref.chapter)
            throw new RefError("Range ends before it starts", input);
        if (ref.endChapter === ref.chapter && ref.verse !== undefined && ref.endVerse !== undefined && ref.endVerse < ref.verse) {
            throw new RefError("Range ends before it starts", input);
        }
        // Drop a degenerate range like Neh 1:3-3.
        if (ref.endChapter === ref.chapter && ref.endVerse === ref.verse) {
            const { endChapter: _c, endVerse: _v, ...single } = ref;
            return single;
        }
    }
    const out = { book: ref.book, chapter: ref.chapter };
    if (ref.verse !== undefined)
        out.verse = ref.verse;
    if (ref.endChapter !== undefined)
        out.endChapter = ref.endChapter;
    if (ref.endVerse !== undefined)
        out.endVerse = ref.endVerse;
    return out;
}
export function isRange(ref) {
    return ref.endChapter !== undefined;
}
/** "Neh.1.3", "Neh.1.3-Neh.1.5", "Neh.1", "Neh.1-Neh.2". */
export function toOsis(ref) {
    const start = ref.verse !== undefined ? `${ref.book}.${ref.chapter}.${ref.verse}` : `${ref.book}.${ref.chapter}`;
    if (ref.endChapter === undefined)
        return start;
    const end = ref.endVerse !== undefined ? `${ref.book}.${ref.endChapter}.${ref.endVerse}` : `${ref.book}.${ref.endChapter}`;
    return `${start}-${end}`;
}
/** "Nehemiah 1:3", "Nehemiah 1:3–5", "Nehemiah 1:3–2:4", "Nehemiah 1", "Jude 5". */
export function toDisplay(ref) {
    const book = bookById(ref.book);
    const name = book?.name ?? ref.book;
    const single = book?.chapters === 1;
    const startV = ref.verse !== undefined ? (single ? `${ref.verse}` : `${ref.chapter}:${ref.verse}`) : `${ref.chapter}`;
    if (ref.endChapter === undefined)
        return `${name} ${startV}`;
    let endV;
    if (ref.endVerse === undefined)
        endV = `${ref.endChapter}`;
    else if (ref.endChapter === ref.chapter || single)
        endV = `${ref.endVerse}`;
    else
        endV = `${ref.endChapter}:${ref.endVerse}`;
    return `${name} ${startV}–${endV}`;
}
/** The query string the ESV API accepts ("Nehemiah 1:3-5"). */
export function toEsvQuery(ref) {
    return toDisplay(ref).replace("–", "-");
}
/** Chapter page slug: "nehemiah/1", "1-john/2". */
export function toSlug(ref) {
    const book = bookById(ref.book);
    const name = (book?.name ?? ref.book).toLowerCase().replace(/\s+/g, "-");
    return `${name}/${ref.chapter}`;
}
export function chapterOf(ref) {
    return { book: ref.book, chapter: ref.chapter };
}
/**
 * A reference cut at its chapter boundaries: "Neh 1:11–2:3" is verse 11 to the
 * end of chapter 1, then verses 1–3 of chapter 2. A whole chapter runs from
 * verse 1 to its end — how far that is, only a text of the chapter can say.
 */
export function chapterSpans(ref) {
    const last = ref.endChapter ?? ref.chapter;
    const spans = [];
    for (let chapter = ref.chapter; chapter <= last; chapter++) {
        const startVerse = chapter === ref.chapter ? (ref.verse ?? 1) : 1;
        let endVerse;
        if (chapter < last)
            endVerse = null;
        else if (ref.endChapter === undefined)
            endVerse = ref.verse ?? null; // one verse, or the whole chapter
        else
            endVerse = ref.endVerse ?? null;
        spans.push({ book: ref.book, chapter, startVerse, endVerse });
    }
    return spans;
}
/** The chapter before/after, crossing book boundaries; undefined at the ends of the canon. */
export function adjacentChapter(ref, dir) {
    const book = bookById(ref.book);
    if (!book)
        return undefined;
    const next = ref.chapter + dir;
    if (next >= 1 && next <= book.chapters)
        return { book: ref.book, chapter: next };
    return undefined;
}
//# sourceMappingURL=refs.js.map